import { useEffect, useRef, useState } from 'react';
import {
  collection, doc, addDoc, setDoc, onSnapshot, orderBy, query,
  serverTimestamp,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase.js';

// TicketChat — the in-app conversation thread that replaced the WhatsApp handoff
// (spec: build/docs/spec-ticket-chat.md). Shared by the requester app, the
// responder app, and the admin ticket drawer (readOnly there).
//
// Writes go DIRECTLY to Firestore, guarded by security rules (participant check,
// caps, closed-ticket lock). Delivery is the realtime listener; pushes to the
// other side come from the onChatMessageCreated trigger.
//
// Props:
//   vendorId, ticket        — the ticket doc (needs id, status, customerName, skillName,
//                             customerUid, assignedTo, helpers)
//   me                      — { uid, name, role }
//   onClose                 — close the modal
//   readOnly (optional)     — force view-only (admin audit view)

const CLOSED_STATUSES = ['CLOSED', 'ABANDONED'];
const MAX_VOICE_MS = 120000; // 2 minutes — long enough to explain, short enough to listen to
const STORAGE_ORIGIN = 'https://firebasestorage.googleapis.com/';

function fmtDur(ms) {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export default function TicketChat({ vendorId, ticket, me, onClose, readOnly = false, footer = null }) {
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  // Voice-note recorder state
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);

  const ticketClosed = CLOSED_STATUSES.includes(ticket.status);
  const isParticipant =
    ticket.customerUid === me.uid
    || ticket.assignedTo?.uid === me.uid
    || (Array.isArray(ticket.helpers) && ticket.helpers.includes(me.uid));
  const canSend = !readOnly && !ticketClosed && isParticipant;

  // Live thread.
  useEffect(() => {
    const q = query(
      collection(db, 'vendors', vendorId, 'tickets', ticket.id, 'messages'),
      orderBy('createdAt', 'asc'),
    );
    return onSnapshot(
      q,
      (snap) => setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
  }, [vendorId, ticket.id]);

  // Mark read on open and whenever new messages land while the thread is open.
  useEffect(() => {
    if (!messages || messages.length === 0 || readOnly) return;
    setDoc(
      doc(db, 'vendors', vendorId, 'tickets', ticket.id, 'reads', me.uid),
      { lastReadAt: serverTimestamp() },
      { merge: true },
    ).catch(() => {});
  }, [vendorId, ticket.id, me.uid, messages?.length, readOnly]);

  // Never leave the microphone live if the modal is closed mid-recording.
  useEffect(() => () => {
    clearInterval(timerRef.current);
    try {
      recorderRef.current?.stream?.getTracks?.().forEach((t) => t.stop());
    } catch { /* already released */ }
  }, []);

  // Stick to the bottom as messages arrive.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages?.length]);

  async function send(e) {
    e?.preventDefault?.();
    const body = text.trim();
    if ((!body && !file) || busy || !canSend) return;
    setBusy(true); setError(null);
    try {
      let photoUrls;
      if (file) {
        const safeName = `${Date.now()}-${file.name.replace(/[^\w.\-]+/g, '_')}`;
        const path = `vendors/${vendorId}/tickets/${ticket.id}/chat/${safeName}`;
        const r = storageRef(storage, path);
        await uploadBytes(r, file);
        photoUrls = [await getDownloadURL(r)];
      }
      await addDoc(collection(db, 'vendors', vendorId, 'tickets', ticket.id, 'messages'), {
        kind: 'user',
        senderUid: me.uid,
        senderName: me.name || 'Me',
        senderRole: me.role || 'user',
        text: body,
        ...(photoUrls ? { photoUrls } : {}),
        createdAt: serverTimestamp(),
      });
      setText('');
      setFile(null);
    } catch (err) {
      setError(prettyChatError(err));
    } finally {
      setBusy(false);
    }
  }

  // ---- Voice notes (2026-08-29) -------------------------------------------
  // A responder mid-job — gloves on, hands full, up a ladder — can hold the
  // conversation without typing. Tap mic → talk → tap Send, or Cancel to bin it.
  async function startRecording() {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Voice notes aren\'t supported on this browser.');
      return;
    }
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
        .find((t) => MediaRecorder.isTypeSupported?.(t)) || '';
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      recorderRef.current = rec;
      startedAtRef.current = Date.now();
      rec.start();
      setRecording(true);
      setElapsedMs(0);
      timerRef.current = setInterval(() => {
        const ms = Date.now() - startedAtRef.current;
        setElapsedMs(ms);
        if (ms >= MAX_VOICE_MS) finishRecording(true);   // hard stop, never runs away
      }, 200);
    } catch {
      setError('Microphone access is needed to record a voice note.');
    }
  }

  function finishRecording(shouldSend) {
    const rec = recorderRef.current;
    if (!rec) return;
    clearInterval(timerRef.current);
    const durationMs = Date.now() - startedAtRef.current;
    rec.onstop = async () => {
      try { rec.stream?.getTracks?.().forEach((t) => t.stop()); } catch { /* mic already released */ }
      recorderRef.current = null;
      setRecording(false);
      setElapsedMs(0);
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
      chunksRef.current = [];
      // Ignore accidental taps rather than posting an empty blip.
      if (!shouldSend || durationMs < 800 || blob.size < 1200) return;
      await sendVoice(blob, durationMs);
    };
    try { rec.stop(); } catch { setRecording(false); }
  }

  async function sendVoice(blob, durationMs) {
    setBusy(true); setError(null);
    try {
      const ext = blob.type.includes('mp4') ? 'm4a' : 'webm';
      const path = `vendors/${vendorId}/tickets/${ticket.id}/chat/voice-${Date.now()}.${ext}`;
      const r = storageRef(storage, path);
      await uploadBytes(r, blob, { contentType: blob.type || 'audio/webm' });
      const url = await getDownloadURL(r);
      await addDoc(collection(db, 'vendors', vendorId, 'tickets', ticket.id, 'messages'), {
        kind: 'user',
        senderUid: me.uid,
        senderName: me.name || 'Me',
        senderRole: me.role || 'user',
        text: '',
        audio: {
          url,
          durationMs: Math.min(Math.round(durationMs), MAX_VOICE_MS),
          mimeType: blob.type || 'audio/webm',
        },
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      setError(prettyChatError(err));
    } finally {
      setBusy(false);
    }
  }

  const otherPartyName = me.uid === ticket.customerUid
    ? (ticket.assignedTo?.name || 'the technician')
    : (ticket.customerName || 'the requester');

  return (
    <div className="ra-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ra-modal" style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <h3 style={{ margin: 0 }}>
            💬 {ticket.skillName}
            <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 14 }}>
              {' '}· {readOnly ? `${ticket.customerName || ''} ↔ ${ticket.assignedTo?.name || '—'}` : otherPartyName}
            </span>
          </h3>
          <button className="btn-ghost" onClick={onClose} style={{ fontSize: 13 }}>Close</button>
        </div>

        <div
          ref={scrollRef}
          style={{
            flex: 1, overflowY: 'auto', margin: '12px 0',
            display: 'flex', flexDirection: 'column', gap: 8,
            padding: '4px 2px', minHeight: 200,
          }}
        >
          {messages === null && <div className="placeholder">Loading conversation…</div>}
          {messages && messages.length === 0 && (
            <div className="placeholder">
              No messages yet. {canSend ? 'Say hello — the other side gets a notification.' : ''}
            </div>
          )}
          {(messages || []).map((m) => <MessageBubble key={m.id} m={m} mine={m.senderUid === me.uid} />)}
        </div>

        {canSend ? (
          <form onSubmit={send} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {file && (
              <div className="file-chip">
                📷 {file.name}
                <button type="button" onClick={() => setFile(null)}>x</button>
              </div>
            )}
            {recording ? (
              <div style={{
                display: 'flex', gap: 10, alignItems: 'center',
                border: '1px solid #fecaca', background: '#fef2f2',
                borderRadius: 'var(--radius)', padding: '10px 12px',
              }}>
                <span style={{
                  width: 11, height: 11, borderRadius: '50%', background: '#dc2626', flex: 'none',
                }} />
                <strong style={{ fontVariantNumeric: 'tabular-nums', fontSize: 16 }}>
                  {fmtDur(elapsedMs)}
                </strong>
                <span style={{ fontSize: 12.5, color: 'var(--muted)', flex: 1 }}>
                  Recording… stops at {fmtDur(MAX_VOICE_MS)}
                </span>
                <button type="button" className="btn-ghost" onClick={() => finishRecording(false)}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={() => finishRecording(true)}>
                  Send
                </button>
              </div>
            ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <label
                className="btn-ghost"
                style={{ padding: '10px 12px', cursor: 'pointer', marginBottom: 0 }}
                title="Attach a photo"
              >
                📷
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ''; }}
                />
              </label>
              <button
                type="button"
                className="btn-ghost"
                style={{ padding: '10px 12px', marginBottom: 0 }}
                title="Record a voice note"
                onClick={startRecording}
                disabled={busy}
              >
                🎤
              </button>
              <textarea
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                maxLength={2000}
                placeholder="Type a message…"
                style={{
                  flex: 1, resize: 'none', padding: '10px 12px',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                  fontFamily: 'inherit', fontSize: 15,
                }}
                disabled={busy}
              />
              <button type="submit" className="btn-primary" disabled={busy || (!text.trim() && !file)}>
                {busy ? '…' : 'Send'}
              </button>
            </div>
            )}
          </form>
        ) : (
          <div className="placeholder" style={{ fontSize: 13 }}>
            {ticketClosed
              ? 'This job is closed — the conversation is kept as a read-only record. Report a new issue if something else comes up.'
              : readOnly
                ? 'Viewing as administrator — read-only.'
                : 'Only the requester and the assigned technician can post here.'}
          </div>
        )}

        {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
        {footer && <div style={{ marginTop: 10 }}>{footer}</div>}
      </div>
    </div>
  );
}

function MessageBubble({ m, mine }) {
  if (m.kind === 'system') {
    return (
      <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--muted)', padding: '2px 0' }}>
        — {m.text} —
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
      <div
        style={{
          maxWidth: '78%',
          background: mine ? '#0f172a' : '#f1f5f9',
          color: mine ? '#fff' : 'inherit',
          borderRadius: 12,
          borderBottomRightRadius: mine ? 4 : 12,
          borderBottomLeftRadius: mine ? 12 : 4,
          padding: '8px 12px',
        }}
      >
        {!mine && (
          <div style={{ fontSize: 11.5, fontWeight: 600, color: '#ea580c', marginBottom: 2 }}>
            {m.senderName}
          </div>
        )}
        {/* Voice note. Same origin check as photos before we hand a URL to the
            media element. */}
        {typeof m.audio?.url === 'string' && m.audio.url.startsWith(STORAGE_ORIGIN) && (
          <div style={{ marginBottom: m.text ? 6 : 0 }}>
            <audio
              controls
              preload="none"
              src={m.audio.url}
              style={{ width: 220, maxWidth: '100%', height: 38 }}
            />
            {m.audio.durationMs > 0 && (
              <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
                🎤 {fmtDur(m.audio.durationMs)}
              </div>
            )}
          </div>
        )}
        {/* Defense-in-depth: rules already restrict photoUrls to our storage
            origin, but never render an href we wouldn't vouch for. */}
        {(m.photoUrls || [])
          .filter((url) => typeof url === 'string' && url.startsWith('https://firebasestorage.googleapis.com/'))
          .map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noreferrer" style={{ display: 'block', marginBottom: m.text ? 6 : 0 }}>
              <img src={url} alt="attachment" style={{ maxWidth: '100%', borderRadius: 8, maxHeight: 240 }} />
            </a>
          ))}
        {m.text && <div style={{ fontSize: 14.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>}
        <div style={{ fontSize: 10.5, opacity: 0.6, marginTop: 3, textAlign: 'right' }}>
          {m.createdAt?.toDate?.()?.toLocaleTimeString?.([], { hour: '2-digit', minute: '2-digit' }) || '…'}
        </div>
      </div>
    </div>
  );
}

function prettyChatError(err) {
  const m = err?.message || '';
  if (m.includes('permission') || m.includes('PERMISSION_DENIED')) {
    return 'Message not sent — this conversation may be closed, or you may not be part of this job.';
  }
  return m || 'Message not sent. Check your connection and try again.';
}

// Small helper used by ticket lists: does this ticket have chat activity the
// given user hasn't seen? Subscribes to the user's read receipt only when the
// ticket actually has chat metadata (cheap — a handful of tiny docs).
export function useThreadUnread(vendorId, ticket, uid) {
  const [lastReadAt, setLastReadAt] = useState(undefined);
  const hasChat = !!ticket?.chat?.lastMessageAt;

  useEffect(() => {
    if (!hasChat || !vendorId || !ticket?.id || !uid) return;
    return onSnapshot(
      doc(db, 'vendors', vendorId, 'tickets', ticket.id, 'reads', uid),
      (snap) => setLastReadAt(snap.exists() ? snap.data().lastReadAt : null),
      () => {},
    );
  }, [hasChat, vendorId, ticket?.id, uid]);

  if (!hasChat) return false;
  if (ticket.chat.lastSenderUid === uid) return false;      // my own message
  if (lastReadAt === undefined) return false;                // still loading — don't flash
  if (lastReadAt === null) return true;                      // never opened the thread
  const lastMsgMs = ticket.chat.lastMessageAt?.toMillis?.() || 0;
  const readMs = lastReadAt?.toMillis?.() || 0;
  return lastMsgMs > readMs;
}
